/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService, GetEnv } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { CustomTextProjectKindSwagger } from 'src/customText/core/services/projects/customTextProjectSwaggers';
import { TestJobCreateSwagger } from 'src/customText/core/services/testModels/customTextTestModelSwaggers';

export const getCustomTextPredictionUrl = (getEnvironment: GetEnv, environmentOptions: GetEnv['arguments'] = {}) => {
    const { webApiUrl, isProduction } = getEnvironment({ apiName: 'customText', ...environmentOptions });
    const stagingBaseUrl = 'https://westus2-tip.api.cognitive.microsoft.com/language/analyze-text';
    const apiVersion = LuisConstants.CustomText.RuntimeApiVersion;

    return urlBuilder()
        .baseUrl(isProduction ? webApiUrl : stagingBaseUrl)
        .resource('jobs')
        .queryParam('api-version', apiVersion)
        .build();
};

export const getCustomTextPredictionCurlRequest = ({
    projectName,
    projectKind,
    deploymentName,
    getEnvironment,
    environmentOptions,
    authorizationHeader
}: {
    projectName: string;
    getEnvironment: GetEnv;
    deploymentName: string;
    authorizationHeader: {
        key: string;
        value: string;
    };
    environmentOptions: GetEnv['arguments'];
    projectKind: CustomTextProjectKindSwagger;
}) => {
    const endpointUrl = getCustomTextPredictionUrl(getEnvironment, environmentOptions);

    return `curl -X POST "${endpointUrl}" -H "${authorizationHeader.key}: ${authorizationHeader.value ??
        'YOUR_ENDPOINT_KEY'}" -H "Content-Type: application/json" -d "${JSON.stringify(
        getCustomTextTestBody(projectName, projectKind, 'YOUR_DOCUMENT_LANGUAGE_HERE', 'YOUR_DOCUMENT_HERE', deploymentName)
    )
        .split('"')
        .join('\\"')}"`;
};

export const getCustomTextTestBody = (
    projectName: string,
    projectKind: CustomTextProjectKindSwagger,
    language: string,
    document: string,
    deploymentName: string
) => {
    const tasksParameters = { projectName, deploymentName };
    const tasks = [];
    let displayedProjectKind = '';

    switch (projectKind) {
        case 'CustomEntityRecognition':
            displayedProjectKind = 'CustomEntityRecognition';
            tasks.push({
                kind: 'CustomEntityRecognition',
                parameters: { projectName, deploymentName, stringIndexType: 'TextElement_v8' }
            });
            break;
        case 'CustomHealthcare':
            displayedProjectKind = 'CustomHealthcare';
            tasks.push({
                kind: 'CustomHealthcare',
                parameters: { projectName, deploymentName, loggingOptOut: false },
                taskName: 'Prediction-CustomHealthcare-Task'
            });
            break;
        case 'CustomMultiLabelClassification':
            displayedProjectKind = 'CustomMultiLabelClassification';
            tasks.push({ kind: 'CustomMultiLabelClassification', parameters: tasksParameters });
            break;
        case 'CustomSingleLabelClassification':
            displayedProjectKind = 'CustomSingleLabelClassification';
            tasks.push({ kind: 'CustomSingleLabelClassification', parameters: tasksParameters });
            break;
        default:
    }

    return {
        tasks,
        displayName: `CustomTextPortal_${displayedProjectKind}`,
        analysisInput: { documents: [{ id: `document_${displayedProjectKind}`, text: document, language }] }
    };
};

/**
 * Custom text model test service.
 */
export const getCustomTextTestModelService = createService(({ getEnvironment }) => {
    const apiOptions = (serviceKey: ServiceKey) =>
        luisRequestOptionsBuilder()
            .useProperServiceKey(serviceKey, true)
            .useContentType('json')
            .build();

    const testModel = async ({
        serviceKey,
        document,
        language,
        projectKind,
        projectName,
        deploymentName,
        operationHandle
    }: {
        document: string;
        language?: string;
        projectName: string;
        deploymentName: string;
        serviceKey: ServiceKey;
        projectKind: CustomTextProjectKindSwagger;
        operationHandle: OperationHandler<TestJobCreateSwagger>;
    }): Promise<TestJobCreateSwagger> => {
        const { headers: testJobHeaders } = await httpService.post<TestJobCreateSwagger>(
            getCustomTextPredictionUrl(getEnvironment),
            getCustomTextTestBody(projectName, projectKind, language, document, deploymentName),
            apiOptions(serviceKey)
        );

        const jobUrl = (<Record<string, string>>testJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
        const { data: testResults } = await createOperationCall<TestJobCreateSwagger>(jobUrl, apiOptions(serviceKey), operationHandle);

        return testResults;
    };

    return { testModel };
});
